import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ExternalLink, Minimize2, ClipboardPaste, Trash2, Tag } from "lucide-react";

// AŞAMA 12 (Adım 2) — İş kolu (görev kolu) sağ tık menüsü. Pano boşken de
// açılır. Maddeler `.filter(Boolean)` ile üretilir (React #130 koruması).
export const CategoryContextMenu = ({
  x,
  y,
  categoryName,
  detached = false,
  clipboardTitle = null,
  onDetach,
  onDock,
  onPaste,
  onClearClipboard,
  onClose,
}) => {
  const ref = useRef();
  const [pos, setPos] = useState({ left: x, top: y, ready: false });

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pad = 8;
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - pad) left = window.innerWidth - rect.width - pad;
    if (top + rect.height > window.innerHeight - pad) top = window.innerHeight - rect.height - pad;
    if (left < pad) left = pad;
    if (top < pad) top = pad;
    setPos({ left, top, ready: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x, y]);

  const items = [
    detached
      ? {
          action: "dock",
          icon: Minimize2,
          label: "GERİ AL (pencereyi kapat)",
          color: "text-emerald-300 hover:bg-emerald-500/15",
          run: onDock,
        }
      : {
          action: "detach",
          icon: ExternalLink,
          label: "PENCEREYE AYIR",
          color: "text-sertex-cyan hover:bg-sertex-cyan/10",
          run: onDetach,
        },
    clipboardTitle && {
      action: "paste",
      icon: ClipboardPaste,
      label: `Yapıştır: ${clipboardTitle}`,
      color: "text-sertex-cyan hover:bg-sertex-cyan/10",
      run: onPaste,
    },
    clipboardTitle && {
      action: "clear",
      icon: Trash2,
      label: "Panoyu Temizle",
      color: "text-rose-300 hover:bg-rose-500/15",
      run: onClearClipboard,
    },
  ].filter(Boolean);

  return createPortal(
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12 }}
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? "visible" : "hidden" }}
      className="fixed z-[130] glass-panel border border-sertex-cyan/40 rounded-md py-1 shadow-lg min-w-[220px]"
      data-testid="category-context-menu"
    >
      <div className="px-3 py-1.5 border-b border-sertex-cyan/20 flex items-center gap-1.5">
        <Tag className="h-3 w-3 text-sertex-cyan shrink-0" />
        <span className="hud-text text-sertex-cyan truncate">{categoryName}</span>
      </div>
      {items.map((it) => (
        <button
          key={it.action}
          onClick={() => {
            it.run?.();
            onClose();
          }}
          data-testid={`cat-ctx-${it.action}`}
          data-cat-ctx={`cat-ctx-${it.action}`}
          className={`w-full text-left px-3 py-2 hud-text flex items-center gap-2 transition-colors ${it.color}`}
        >
          <it.icon className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">{it.label}</span>
        </button>
      ))}
    </motion.div>,
    document.body,
  );
};

export default CategoryContextMenu;
